import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "./EditProfile.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ProfileEdit() {
  const { userInfo, isLoggedIn, authFetch, csrfToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!isLoggedIn || !userInfo) {
      navigate("/login");
      return;
    }

    if (userInfo.role !== "user") {
      navigate("/dashboard");
      return;
    }

    setLoading(true);
    authFetch(`${API_BASE_URL}/Users/${userInfo.userId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        return response.json();
      })
      .then((data) => {
        setUsername(data.username || "");
        setEmail(data.email || "");
        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setAddress(data.address || "");
        setCity(data.city || "");
        setState(data.state || "");
        setZipCode(data.zipCode || "");
        setPhoneNumber(data.phoneNumber || "");
        setLoading(false);
      })
      .catch((err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not load user data. Please try again later.',
        });
        setLoading(false);
      });
  }, [userInfo, isLoggedIn, navigate, authFetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (password && password !== confirmPassword) {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Password Error',
        text: 'Passwords don\'t match',
      });
      return;
    }

    try {
      const userData = {
        userID: userInfo.userId,
        username: username,
        email: email,
        firstName: firstName,
        lastName: lastName,
        address: address,
        city: city,
        state: state,
        zipCode: zipCode,
        phoneNumber: phoneNumber
      };

      if (password) {
        userData.password = password;
      }

      const response = await authFetch(`${API_BASE_URL}/Users/${userInfo.userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": csrfToken
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update profile");
      }

      const updatedUser = await response.json();

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Profile updated successfully!',
        showConfirmButton: false,
        timer: 1500
      });

      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.message || 'Failed to update profile. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: 'Discard Changes?',
      text: "Any unsaved changes will be lost.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, continue editing'
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/dashboard");
      }
    });
  };

  const statesList = [
    "Johor",
    "Kedah",
    "Kelantan",
    "Melaka",
    "Negeri Sembilan",
    "Pahang",
    "Penang",
    "Perak",
    "Perlis",
    "Sabah",
    "Sarawak",
    "Selangor",
    "Terengganu",
    "Kuala Lumpur",
  ];

  if (loading && !userInfo) {
    return (
      <div className="profile-edit-container">
        <div className="profile-edit-loading">
          <div className="profile-edit-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-edit-container">
      <div className="profile-edit-sub-container">
        <div className="profile-edit-header">
          <h1 className="profile-edit-title">
            <i className="fas fa-user-edit"></i> Edit Your Profile
          </h1>
          <Link to="/dashboard" className="profile-edit-back">
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </Link>
        </div>

        {error && <div className="profile-edit-error">{error}</div>}
        {success && <div className="profile-edit-success">{success}</div>}

        <form className="profile-edit-form" onSubmit={handleSubmit}>
          <div className="profile-edit-form-grid">
            {/* Personal Information Section */}
            <div className="profile-edit-section">
              <h2 className="profile-edit-section-title">
                <i className="fas fa-user"></i> Personal Information
              </h2>

              <div className="profile-edit-fields-row">
                <div className="profile-edit-field">
                  <label htmlFor="firstName" className="profile-edit-label">
                    <i className="fas fa-user-alt profile-edit-icon"></i> First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="profile-edit-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>

                <div className="profile-edit-field">
                  <label htmlFor="lastName" className="profile-edit-label">
                    <i className="fas fa-user-alt profile-edit-icon"></i> Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="profile-edit-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="profile-edit-fields-row">
                <div className="profile-edit-field">
                  <label htmlFor="username" className="profile-edit-label">
                    <i className="fas fa-id-badge profile-edit-icon"></i> Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    className="profile-edit-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                <div className="profile-edit-field">
                  <label htmlFor="email" className="profile-edit-label">
                    <i className="fas fa-envelope profile-edit-icon"></i> Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="profile-edit-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="profile-edit-field">
                <label htmlFor="phoneNumber" className="profile-edit-label">
                  <i className="fas fa-phone profile-edit-icon"></i> Phone Number
                </label>
                <input
                  type="text"
                  id="phoneNumber"
                  className="profile-edit-input"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="profile-edit-section">
              <h2 className="profile-edit-section-title">
                <i className="fas fa-map-marker-alt"></i> Address Information
              </h2>

              <div className="profile-edit-field">
                <label htmlFor="address" className="profile-edit-label">
                  <i className="fas fa-home profile-edit-icon"></i> Street Address
                </label>
                <input
                  type="text"
                  id="address"
                  className="profile-edit-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div className="profile-edit-fields-row">
                <div className="profile-edit-field">
                  <label htmlFor="city" className="profile-edit-label">
                    <i className="fas fa-city profile-edit-icon"></i> City
                  </label>
                  <input
                    type="text"
                    id="city"
                    className="profile-edit-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>

                <div className="profile-edit-field">
                  <label htmlFor="state" className="profile-edit-label">
                    <i className="fas fa-map profile-edit-icon"></i> State
                  </label>
                  <select
                    id="state"
                    className="profile-edit-input"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="">Select State</option>
                    {statesList.map((stateName) => (
                      <option key={stateName} value={stateName}>
                        {stateName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="profile-edit-field">
                <label htmlFor="zipCode" className="profile-edit-label">
                  <i className="fas fa-map-pin profile-edit-icon"></i> Zip Code
                </label>
                <input
                  type="text"
                  id="zipCode"
                  className="profile-edit-input"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                />
              </div>
            </div>

            {/* Password Section */}
            <div className="profile-edit-section password-section">
              <h2 className="profile-edit-section-title">
                <i className="fas fa-lock"></i> Change Password
              </h2>
              <div className="profile-edit-password-note">
                <i className="fas fa-info-circle"></i> Leave blank to keep current password
              </div>

              <div className="profile-edit-fields-row">
                <div className="profile-edit-field">
                  <label htmlFor="password" className="profile-edit-label">
                    <i className="fas fa-key profile-edit-icon"></i> New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    className="profile-edit-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div className="profile-edit-field">
                  <label htmlFor="confirmPassword" className="profile-edit-label">
                    <i className="fas fa-check-circle profile-edit-icon"></i> Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    className="profile-edit-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="profile-edit-buttons">
            <button type="submit" className="profile-edit-submit" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              className="profile-edit-cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileEdit;